import React from 'react';
import styled from 'styled-components';
import { useMsal } from '@azure/msal-react';
import { resetSessionExpired } from '../authSession';
import { loginRequest, DEV_MODE } from '../authConfig';
import { useDevAuth } from './DevAuthProvider';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: 
              url('https://images.unsplash.com/photo-1664902276790-90624a60ff47?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: 20px;
`;

const LoginMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 40px;
  max-width: 400px;
  width: 100%;
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`;

const LogoImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin-bottom: 16px;
  border-radius: 12px;
  background: white;
  padding: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  text-align: center;
  line-height: 1.3;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 28px;
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.5;
`;

const LoginButton = styled.button`
  background: linear-gradient(135deg, #0078d4 0%, #106ebe 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  
  &:hover {
    background: linear-gradient(135deg, #106ebe 0%, #005a9e 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 120, 212, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const MicrosoftIcon = styled.div`
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  color: #0078d4;
`;

const InfoText = styled.p`
  color: #95a5a6;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const DevModeNotice = styled.div`
  background-color: #fff3cd;
  color: #856404;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 12px;
  border: 1px solid #ffeaa7;
  text-align: center;
`;

const SessionExpiredNotice = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  border: 1px solid #fcc;
  text-align: center;
  line-height: 1.4;
`;

const DevLoginButton = styled.button`
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  
  &:hover {
    background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(40, 167, 69, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  border: 1px solid #fcc;
`;

const LoginFooter = styled.footer`
  background: rgba(0, 0, 0, 0.2);
  color: white;
  padding: 20px;
  text-align: center;
  backdrop-filter: blur(10px);
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const FooterText = styled.div`
  font-size: 14px;
  opacity: 0.9;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const FooterLink = styled.a`
  color: white;
  text-decoration: none;
  font-size: 14px;
  opacity: 0.9;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

interface LoginPageProps {
  sessionExpired?: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ sessionExpired = false }) => {
  const { instance } = useMsal();
  const devAuth = DEV_MODE ? useDevAuth() : null;

  const handleLogin = async (): Promise<void> => {
    if (DEV_MODE && devAuth) {
      devAuth.login();
      resetSessionExpired();
      return;
    }

    try {
      if (sessionExpired) {
        await instance.logoutPopup({ mainWindowRedirectUri: window.location.origin });
      }
      const response = await instance.loginPopup(loginRequest);
      console.log('Login successful:', response);
      resetSessionExpired();
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <LoginContainer>
      <LoginMain>
        <LoginCard>
          <LogoContainer>
            <LogoImage 
              src="https://www.chcahealth.org/wp-content/uploads/2023/12/main-logop.png" 
              alt="CHCA Logo"
            />
            <LogoText>
              COMMUNITY HEALTH CENTERS<br />
              OF AMERICA
            </LogoText>
          </LogoContainer>
          <Title>LACE Tool</Title>
          <Subtitle>
            California Post-Acute Care Services<br />
            Physician Extender Portal
          </Subtitle>
          
          {sessionExpired && !DEV_MODE && (
            <SessionExpiredNotice>
              Your session has expired. Sign in again to continue using the LACE Tool.
            </SessionExpiredNotice>
          )}

          {DEV_MODE && (
            <DevModeNotice>
              🚧 Development Mode - Azure AD authentication is disabled
            </DevModeNotice>
          )}
          
          {DEV_MODE ? (
            <DevLoginButton onClick={handleLogin}>
              🚀 Continue to LACE Tool (Dev Mode)
            </DevLoginButton>
          ) : (
            <LoginButton onClick={handleLogin}>
              <MicrosoftIcon>M</MicrosoftIcon>
              Sign in with Microsoft
            </LoginButton>
          )}
          
          <InfoText>
            {DEV_MODE 
              ? "Development mode is active. Click the button above to access the LACE Tool without authentication."
              : "Please sign in with your organizational Microsoft account to access the LACE Tool assessment system."
            }
          </InfoText>
        </LoginCard>
      </LoginMain>
      
      <LoginFooter>
        <FooterContent>
          <FooterText>
            © 2025 Community Health Centers of America. All rights reserved.
          </FooterText>
          <FooterLinks>
            <FooterLink href="https://www.chcahealth.org/privacy-policy/">Privacy Policy</FooterLink>
            <FooterLink href="https://www.chcahealth.org/terms-and-conditions/">Terms of Service</FooterLink>
            <FooterLink href="#">Support</FooterLink>
            <FooterLink href="https://www.chcahealth.org/contact-us/">Contact</FooterLink>
          </FooterLinks>
        </FooterContent>
      </LoginFooter>
    </LoginContainer>
  );
}

export default LoginPage;
